import {
  Component,
  HostListener,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { NgProgress } from '@ngx-progressbar/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import {
  FileRestrictions,
  FileState,
  SelectEvent,
  UploadComponent
} from '@progress/kendo-angular-upload';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import * as XLSX from 'xlsx';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { ExportToExcelService } from '../../../../shared/services/export-to-excel.service';
import {
  UIActiveDialogService,
  UIDialogService
} from '../../../../shared/services/ui-dialog.service';
import { AuthService } from '../../../security/service/auth.service';
import { ProductUpsertArrayModel } from '../../model/products/bulk-add-product-array.model';
import { ProductUpsertModel } from '../../model/products/bulk-add-product.model';
import { BulkAddProductUIMessages } from '../../model/products/bulk-add-products-ui-messages';
import { BulkAddUIMessages } from '../../model/products/bulk-add-warranty-ui-messages';
import { BulkAddProductsService } from '../../service/bulk-add-products.service';
import { BulkAddProgressDialog } from '../../shared/bulk-add-progress-dialog/bulk-add-progress-dialog.component';
import { PleaseCheckData } from '../../shared/please-check-grid-dialog/please-check-grid-dialogue.component';

@Component({
  selector: 'bulk-add-products',
  encapsulation: ViewEncapsulation.None,
  templateUrl:
    'console/app-js/modules/bulkAdd/components/products/bulk-add-products.component.html'
})
export class BulkAddProductsComponent extends APIOperableComponent {
  protected data: any = [];
  protected numProducts: number;
  protected errorCountSubmit = 0;
  protected indexOfRecordsSent = 1;
  protected failedRows = '';
  protected errorArr: any = [];
  private errorArrFront: any = [];
  protected gridView: GridDataResult;
  protected pageSize = 10;
  protected skip = 0;
  public fileState = FileState;
  private productObservables: Observable<any>[] = [];
  private serverErroredProducts = [];

  private BATCH_REQUEST_NUMBER = 50;
  private EXCEL_FILE_NAME = 'BulkAddProductError.xlsx';

  // block all other file types other than .xlsx files
  public uploadRestrictions: FileRestrictions = {
    allowedExtensions: ['.xlsx']
  };
  protected templateText = 'Product Upload Template';

  private updatedItems: any = [];
  private createdItems: any = [];
  private recordsSent = 0;
  private errorTrys = 0;

  @ViewChild('fileUpload', { static: true }) fileUpload: UploadComponent;

  constructor(
    private exportToExcelService: ExportToExcelService,
    private bulkAddProductsService: BulkAddProductsService,
    private dialogOpenService: UIDialogService,
    private dialogCloseService: UIActiveDialogService,
    private sanitizer: DomSanitizer,
    toastr: ToastrService,
    progressService: NgProgress,
    authService: AuthService,
    private formBuilder: FormBuilder
  ) {
    super(progressService, toastr, authService);
  }

  /**
   if there are updated items in grid don't let the user move away from page.
   Function is called in parent class
 */
  @HostListener('window:beforeunload')
  canDeactivate() {
    return this.ableToNavigateAway();
  }

  ableToNavigateAway(): boolean {
    if (this.updatedItems.length > 0) {
      return false;
    } else {
      return true;
    }
  }

  // Kendo UI Upload select event, when a file is selected for upload
  public selectEventHandler(event: SelectEvent): void {
    // testing
    const self = this;
    self.resetErrorGrid();

    event.files.forEach((file: any) => {
      if (!file.validationErrors) {
        const reader: FileReader = new FileReader();

        reader.onload = function(e: any) {
          /* read workbook */
          /* with base64 have to replace the beginning part of the string that tells what format it is in in order for it to decode properly */
          const bstr: string = e.target.result.replace(
            /^data:(.*?);base64,/,
            ''
          );
          const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'base64' });

          /* grab first sheet */
          const wsname: string = wb.SheetNames[0];
          const ws: XLSX.WorkSheet = wb.Sheets[wsname];

          /* save data */
          self.data = XLSX.utils.sheet_to_json(ws, { blankrows: false });
          const stringFields = [
            'ID',
            'Status',
            'Type',
            'Make',
            'Model',
            'AssetType',
            'RequiresAudit',
            'LineOfService1',
            'LineOfService2',
            'LineOfService3',
            'LineOfService4',
            'LineOfService5'
          ];
          //Checking if asset upload excel has data to upload
          const hasDataToUpload =
            self != null &&
            self.data != null &&
            self.data !== undefined &&
            self.data.length > 0;
          if (hasDataToUpload) {
            for (let i = 0; i < self.data.length; i++) {
              const obj = self.data[i];
              for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                  if (
                    stringFields.indexOf(key) !== -1 &&
                    obj[key] !== null &&
                    obj[key] !== undefined
                  ) {
                    obj[key] = obj[key].toString();
                  }
                }
              }
            }
          }

          self.bulkAddProductValidation(self.data);
        };

        reader.readAsDataURL(file.rawFile);
      } else {
        console.error('Error encountered in selectEventHandler.');
      }
    });
  }

  // Reset grid between uploads so that the grid of errors is only for the current upload
  private resetErrorGrid() {
    const self = this;

    self.gridView = {
      data: null,
      total: 0
    };

    // reset the grid page to zero
    self.skip = 0;

    self.errorArr = [];
    self.errorArrFront = [];
    self.numProducts = 0;
    self.failedRows = '';
  }
  /**
   *
   * @param product
   * validates each row for errors before submuitting
   */
  private rowValidation(product: any): any {
    let colArr = [];
    for (let x = 0; x < Object.keys(product).length - 2; x++) {
      if (
        Object.keys(product).map(key => product[key])[x] === '' &&
        Object.keys(product)[x] !== 'LineOfService2' &&
        Object.keys(product)[x] !== 'LineOfService3' &&
        Object.keys(product)[x] !== 'LineOfService4' &&
        Object.keys(product)[x] !== 'LineOfService5' &&
        Object.keys(product)[x] !== 'ID' &&
        Object.keys(product)[x] !== 'PartWarrantyDays' &&
        Object.keys(product)[x] !== 'LaborWarrantyDays'
      ) {
        colArr.push(
          Object.keys(product)
            [x].charAt(0)
            .toLowerCase() + Object.keys(product)[x].slice(1)
        );

        product.Issues =
          product.Issues === undefined
            ? BulkAddProductUIMessages[Object.keys(product)[x]]
            : product.Issues +
              '; ' +
              BulkAddProductUIMessages[Object.keys(product)[x]];
      }
    }

    if (product.ID !== undefined && (isNaN(product.ID) || product.ID < 0)) {
      product.Issues =
        product.Issues === undefined
          ? BulkAddUIMessages.MachineProductIdInvalid
          : product.Issues + '; ' + BulkAddUIMessages.MachineProductIdInvalid;
      colArr.push('id');
    }

    if (
      product.PartWarrantyDays !== undefined &&
      (isNaN(product.PartWarrantyDays) || product.PartWarrantyDays < 0)
    ) {
      product.Issues =
        product.Issues === undefined
          ? BulkAddUIMessages.InvalidPartWarranty
          : product.Issues + '; ' + BulkAddUIMessages.InvalidPartWarranty;
      colArr.push('partWarrantyDays');
    }

    if (
      product.LaborWarrantyDays !== undefined &&
      (isNaN(product.LaborWarrantyDays) || product.LaborWarrantyDays < 0)
    ) {
      product.Issues =
        product.Issues === undefined
          ? BulkAddUIMessages.InvalidLaborWarranty
          : product.Issues + '; ' + BulkAddUIMessages.InvalidLaborWarranty;
      colArr.push('laborWarrantyDays');
    }

    if (colArr.length > 0) {
      product.Column = colArr;
      this.errorArrFront.push(product);
      return true;
    } else {
      delete product.Issues;
      delete product.Column;
      delete product.Column;
      return product;
    }
  }
  /**
   *
   * @param res
   * formats data being returned form backend
   */
  private formatBadErrors(res) {
    let badProductArray = [];
    let loadedRecords = 0;
    if (this.serverErroredProducts.length > 0) {
      for (
        let errorNumber = 0;
        errorNumber < this.serverErroredProducts.length;
        errorNumber++
      ) {
        this.failedRows +=
          (
            this.serverErroredProducts[errorNumber] *
              this.BATCH_REQUEST_NUMBER -
            this.BATCH_REQUEST_NUMBER +
            1
          ).toString() +
          '- ' +
          (
            this.serverErroredProducts[errorNumber] * this.BATCH_REQUEST_NUMBER
          ).toString() +
          ' ';
      }
    }
    for (let obs of res) {
      loadedRecords += obs.loadedRecords;
      badProductArray = badProductArray.concat(obs.badProductsList);
    }
    badProductArray.forEach(badProduct => {
      Object.keys(badProduct.value)
        .map(key => badProduct.value[key])
        .forEach(val => {
          badProduct.key.Issues =
            badProduct.key.Issues === undefined ||
            badProduct.key.Issues.includes(
              'A product with that Type/Make/Model combination already exists'
            ) ||
            (badProduct.key.Issues.includes(
              'Please add a known Line of Service'
            ) &&
              val === 'Please add a known Line of Service')
              ? val
              : badProduct.key.Issues + '; ' + val;
        });
      badProduct.key.Column = Object.keys(badProduct.value);
      this.errorArr.push(this.mapToUpsertProductModelBE(badProduct.key));
    });

    this.numProducts = loadedRecords;
    if (this.numProducts === this.recordsSent) {
      this.onAPISuccess(`${this.recordsSent} Products Uploaded Succesfully`);
    } else if (this.errorArr.length > 0) {
      this.onAPIError(
        `${this.errorArr.length +
          this.errorArrFront.length} Products with errors`
      );
    }
    this.dialogCloseService.close(BulkAddProgressDialog);
    this.errorArr = this.errorArr.concat(this.errorArrFront);
    this.loadItems();
  }
  /**
   *
   * @param products
   * starts the frontend validation logic
   */
  private bulkAddProductValidation(products: ProductUpsertModel[]) {
    this.recordsSent = products.length;
    let upsertProducts = new ProductUpsertArrayModel();
    upsertProducts.upsertArray = [];
    let ProductUpsertArrayModelArr: ProductUpsertArrayModel[] = [];
    this.dialogOpenService.open(BulkAddProgressDialog, []);
    for (let x = 1; x <= products.length; x++) {
      let product = this.mapToUpsertProductModelCS(products[x - 1]);
      product.Issues = undefined;
      let productUpload = this.rowValidation(product);
      if (productUpload !== true) {
        upsertProducts.upsertArray.push(product);
      }
      if (x % this.BATCH_REQUEST_NUMBER === 0 || products.length - x === 0) {
        ProductUpsertArrayModelArr.push(Object.assign({}, upsertProducts));
        upsertProducts.upsertArray = [];
      }
    }
    this.concurrentUpload(ProductUpsertArrayModelArr);
  }
  /**
   *
   * @param badProduct
   * maps data being returned from backend to frontend model
   */
  private mapToUpsertProductModelBE(badProduct: any): ProductUpsertModel {
    let badProductUpsert = new ProductUpsertModel();
    badProductUpsert.ID = badProduct.id;
    badProductUpsert.Status = badProduct.status;
    badProductUpsert.Type = badProduct.type;
    badProductUpsert.Make = badProduct.make;
    badProductUpsert.Model = badProduct.model;
    badProductUpsert.AssetType = badProduct.assetType;
    badProductUpsert.RequiresAudit = badProduct.requiresAudit;
    badProductUpsert.LineOfService1 = badProduct.lineOfService1;
    badProductUpsert.LineOfService2 = badProduct.lineOfService2;
    badProductUpsert.LineOfService3 = badProduct.lineOfService3;
    badProductUpsert.LineOfService4 = badProduct.lineOfService4;
    badProductUpsert.LineOfService5 = badProduct.lineOfService5;
    badProductUpsert.LaborWarrantyDays = badProduct.laborWarrantyDays;
    badProductUpsert.PartWarrantyDays = badProduct.partWarrantyDays;
    badProductUpsert.Issues = badProduct.Issues;
    badProductUpsert.Column = badProduct.Column;
    return badProductUpsert;
  }
  /**
   *
   * @param product
   * adds empty columns for data that doesn't have data
   */
  private mapToUpsertProductModelCS(product: any): ProductUpsertModel {
    product = this.trimObj(product);
    let productUpsert = new ProductUpsertModel();
    productUpsert.ID = product.hasOwnProperty('ID') ? product.ID : '';
    productUpsert.Status = product.hasOwnProperty('Status')
      ? product.Status
      : '';
    productUpsert.Type = product.hasOwnProperty('Type') ? product.Type : '';
    productUpsert.Make = product.hasOwnProperty('Make') ? product.Make : '';
    productUpsert.Model = product.hasOwnProperty('Model') ? product.Model : '';
    productUpsert.AssetType = product.hasOwnProperty('AssetType')
      ? product.AssetType
      : '';
    productUpsert.RequiresAudit = product.hasOwnProperty('RequiresAudit')
      ? product.RequiresAudit
      : '';
    productUpsert.LineOfService1 = product.hasOwnProperty('LineOfService1')
      ? product.LineOfService1
      : '';
    productUpsert.LineOfService2 = product.hasOwnProperty('LineOfService2')
      ? product.LineOfService2
      : '';
    productUpsert.LineOfService3 = product.hasOwnProperty('LineOfService3')
      ? product.LineOfService3
      : '';
    productUpsert.LineOfService4 = product.hasOwnProperty('LineOfService4')
      ? product.LineOfService4
      : '';
    productUpsert.LineOfService5 = product.hasOwnProperty('LineOfService5')
      ? product.LineOfService5
      : '';
    productUpsert.LaborWarrantyDays = product.hasOwnProperty(
      'LaborWarrantyDays'
    )
      ? product.LaborWarrantyDays
      : null;
    productUpsert.PartWarrantyDays = product.hasOwnProperty('PartWarrantyDays')
      ? product.PartWarrantyDays
      : null;

    productUpsert.Issues = product.Issues;
    productUpsert.Column = [];
    return productUpsert;
  }

  private trimObj(o: any) {
    return JSON.parse(JSON.stringify(o).replace(/"\s+|\s+"/g, '"'));
  }
  /**
   * updates session token for bulk upload
   */
  private updateSessionDuringBulkUpload() {
    let sessionGauge = 60 * 60 * 1500;
    let expiresTime = localStorage.expires_at;
    let currentTime = Date.now();
    if (expiresTime - currentTime <= sessionGauge) {
      this.authService.getConfigAndInitAuth0().subscribe(() => {
        this.authService.renewToken();
      });
    }
  }

  private addAPITimeoutErrorMessage(products) {
    for (let i = 0; i < products.upsertArray.length; i++) {
      products.upsertArray[i].Issues = 'API error, Please resubmit record';
      products.upsertArray[i].Column = ['ID'];
      this.errorArrFront.push(
        this.mapToUpsertProductModelCS(products.upsertArray[i])
      );
    }
  }

  /**
   *
   * @param upsertProductsArray
   * calls service for upload
   */
  private concurrentUpload(upsertProductsArray: ProductUpsertArrayModel[]) {
    this.bulkAddProductsService.productUpsert(upsertProductsArray[0]).subscribe(
      (res: any) => {
        // add results to productObservables
        this.productObservables.push(res);
      },
      err => {
        if (upsertProductsArray.length === 0) {
          this.dialogCloseService.close(BulkAddProgressDialog);
          this.formatBadErrors(this.productObservables);
          this.productObservables = [];
          this.onAPIError('Error Uploading Assets');
        } else {
          if (this.errorTrys < 2) {
            this.errorTrys++;
            this.concurrentUpload(upsertProductsArray);
          } else {
            this.errorTrys = 0;
            this.addAPITimeoutErrorMessage(upsertProductsArray[0]);
            upsertProductsArray = upsertProductsArray.slice(1);
            this.concurrentUpload(upsertProductsArray);
          }
        }
      },
      () => {
        upsertProductsArray = upsertProductsArray.slice(1);
        this.errorTrys = 0;
        if (upsertProductsArray.length === 0) {
          this.formatBadErrors(this.productObservables);
          this.productObservables = [];
        } else {
          this.indexOfRecordsSent++;
          this.concurrentUpload(upsertProductsArray);
        }
      }
    );
  }

  protected pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.loadItems();
  }

  /**
   * loads kendo grid data
   */
  private loadItems(): void {
    this.gridView = {
      data: this.errorArr.slice(this.skip, this.skip + this.pageSize),
      total: this.errorArr.length
    };
  }

  /**
   *
   * @param record
   * @param index
   * @param dataFromRow
   * color codes cells for errors
   */
  private colorCode(record: string[], index: number, dataFromRow): SafeStyle {
    let result: string;
    for (let col of record) {
      if (this.rightIndex(col, index)) {
        if (
          dataFromRow.ColumnsEdited !== undefined &&
          dataFromRow.ColumnsEdited.includes(col)
        ) {
          result = '#98FB98';
        } else {
          result = '#f4f776';
        }
      }
    }
    return this.sanitizer.bypassSecurityTrustStyle(result);
  }

  /**
   *
   * @param columnName
   * @param index
   * maps column index for color codes
   */
  private rightIndex(columnName: string, index: number): boolean {
    const COL_INDICES = {
      id: 0,
      status: 1,
      type: 2,
      make: 3,
      model: 4,
      assetType: 5,
      requiresAudit: 6,
      lineOfService1: 7,
      lineOfService2: 8,
      lineOfService3: 9,
      lineOfService4: 10,
      lineOfService5: 11,
      laborWarrantyDays: 12,
      partWarrantyDays: 13
    };
    if (COL_INDICES[columnName] === index) {
      return true;
    } else {
      return false;
    }
    // return COL_INDICES[columnName] ? COL_INDICES[columnName] === index : false;
  }

  // Start of in cell editing functions
  public cellClickHandler({
    sender,
    rowIndex,
    column,
    columnIndex,
    dataItem,
    isEdited
  }) {
    let values = Object.keys(dataItem)
      .map(key => dataItem[key])
      .splice(1);
    if (!isEdited && this.isWriteOnly(column.field, dataItem.Column)) {
      sender.editCell(rowIndex, columnIndex, this.createFormGroup(dataItem));
    }
  }
  /**
   *
   * @param args
   * cell close handle kendo grid
   */
  public cellCloseHandler(args: any) {
    const { formGroup, dataItem } = args;

    if (!formGroup.valid) {
      // prevent closing the edited cell if there are invalid values.
      args.preventDefault();
    } else if (formGroup.dirty) {
      if (dataItem.ColumnsEdited === undefined) {
        dataItem.ColumnsEdited = [];
      }
      if (args.column.field === 'ID') {
        args.column.field = args.column.field.toLowerCase();
      }
      if (
        !dataItem.ColumnsEdited.includes(
          args.column.field.charAt(0).toLowerCase() + args.column.field.slice(1)
        )
      ) {
        dataItem.ColumnsEdited.push(
          args.column.field.charAt(0).toLowerCase() + args.column.field.slice(1)
        );
      }
      if (args.column.field === 'id') {
        args.column.field = args.column.field.toUpperCase();
      }
      this.assignValues(dataItem, formGroup.value);
      this.update(dataItem, args.rowIndex);
    }
  }
  /**
   * grid check if changes
   */
  public hasChanges(): boolean {
    return Boolean(this.updatedItems.length || this.createdItems.length);
  }

  public assignValues(target: any, source: any): void {
    Object.assign(target, source);
  }
  /**
   *
   * @param item
   * @param index
   * updates updatedItems Array for upload from cell editing
   */
  public update(item: any, index): void {
    let x = this.updatedItems.length;
    let modifiedExistingRow = false;
    item.rowIndex = index;
    // check if row has already been modified then replace existing object in updated items array
    for (let i = 0; i < x; i++) {
      if (index === this.updatedItems[i].rowIndex) {
        this.updatedItems[i] = item;
        modifiedExistingRow = true;
        break;
      }
    }
    if (modifiedExistingRow === false) {
      this.updatedItems.push(item);
    }
  }

  public saveHandler({ sender, formGroup, rowIndex }) {
    if (formGroup.valid) {
      sender.closeRow(rowIndex);
    }
  }
  /**
   *
   * @param grid
   * starts the save process for edited changes
   */
  public saveChanges(grid: any): void {
    grid.closeCell();
    grid.cancelCell();
    let rowsCanBeSubmitted = true;
    // add logic to submit data
    for (let t = 0; t < this.updatedItems.length; t++) {
      if (
        !(
          this.updatedItems[t].Column.length ===
          this.updatedItems[t].ColumnsEdited.length
        )
      ) {
        rowsCanBeSubmitted = false;
        break;
      }
    }
    if (rowsCanBeSubmitted) {
      for (let j = 0; j < this.updatedItems.length; j++) {
        for (let i = 0; i < this.errorArr.length; i++) {
          if (
            this.errorArr[i].hasOwnProperty('rowIndex') &&
            this.errorArr[i].rowIndex === this.updatedItems[j].rowIndex
          ) {
            this.errorArr.splice(i, 1);
            break; // so that it doesn't keep looping, after finding a match
          }
        }
      }
      let t0 = Date.now();
      this.errorArrFront = [];
      this.bulkAddProductValidation(this.updatedItems);
      this.updatedItems = [];
    } else {
      this.dialogOpenService.open(PleaseCheckData, []);
    }
  }

  /**
   *
   * @param dataItem
   * form group used for cell editing
   */

  public createFormGroup(dataItem: any): FormGroup {
    return this.formBuilder.group({
      ID: dataItem.ID,
      Status: dataItem.Status,
      Type: dataItem.Type,
      Make: dataItem.Make,
      Model: dataItem.Model,
      AssetType: dataItem.AssetType,
      RequiresAudit: dataItem.RequiresAudit,
      LineOfService1: dataItem.LineOfService1,
      LineOfService2: dataItem.LineOfService2,
      LineOfService3: dataItem.LineOfService3,
      LineOfService4: dataItem.LineOfService4,
      LineOfService5: dataItem.LineOfService5,
      LaborWarrantyDays: dataItem.LaborWarrantyDays,
      PartWarrantyDays: dataItem.PartWarrantyDays
    });
  }

  /**
   *
   * @param field
   * @param columns
   * checks if cell is an editable field
   */
  private isWriteOnly(field: string, columns: any): boolean {
    let writeOnlyColumns = columns.slice();
    for (let i = 0; i < writeOnlyColumns.length; i++) {
      writeOnlyColumns[i] = writeOnlyColumns[i].toUpperCase();
    }
    let fieldStuff = field.toUpperCase();
    let test = writeOnlyColumns.indexOf(fieldStuff);
    return test >= 0;
  }
  // end of in cell editing functions

  public onExportResults(): void {
    let jsonData = this.errorArr.map((row: any) => {
      return {
        ID: row.ID,
        Status: row.Status,
        Type: row.Type,
        Make: row.Make,
        Model: row.Model,
        AssetType: row.AssetType,
        RequiresAudit: row.RequiresAudit,
        LineOfService1: row.LineOfService1,
        LineOfService2: row.LineOfService2,
        LineOfService3: row.LineOfService3,
        LineOfService4: row.LineOfService4,
        LineOfService5: row.LineOfService5,
        LaborWarrantyDays: row.LaborWarrantyDays,
        PartWarrantyDays: row.PartWarrantyDays,
        Issues: row.Issues
      };
    });
    this.exportToExcelService.exportFromJsonToExcel(
      jsonData,
      this.EXCEL_FILE_NAME,
      'xlsx'
    );
  }
}
